/**
 * PACKAGE
 * LOCALIZATION
 * CONSTANTS
 * UTILITY
 * STORE
 * HOC COMPONENTS
 * COMPONENTS
 * ASSETS
 * HELP
 * STYLES
 */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';

// CONSTANTS
import { siteUrl } from '@/constants/common';
import { ROUTE_STREAMTV, ROUTE_PARTNER_STREAMTV } from '@/constants/routes';

// UTILITY
import { analyticsPageLoaded } from '@/utility/analytics';
import { getS3ImageUrl } from '@/utility/content';
import { getLink } from '@/utility/routes';

// HOC
import Layout from '@/hoc/Layout';
import Guard from '@/hoc/Guard';
import withRouter from '@/hoc/withRouter';

// COMPONENTS
import FreeTVSection from '@/components/FreeTVSection';

// HELP
import Welcome from './help/Welcome';
import ShowsAndMovies from './help/ShowsAndMovies';
import Questions from './help/Questions';
import SellInternet from './help/SellInternet';

// STYLES
import { Container } from './styles';

const defaultOGImg = getS3ImageUrl('/og-image/og-default.png');

class PartnerStreamTV extends PureComponent {
  componentDidMount() {
    window.prerendercloudReady = false;
  }

  handleChangeHelmetData = () => {
    if (this.lastPath !== window.location.pathname) {
      analyticsPageLoaded();
      this.lastPath = window.location.pathname;

      window.prerendercloudReady = true;
    }
  };

  render() {
    const { isIframe, partner, partnerLoading } = this.props;

    const isPartner = !!partner.slug;

    const seoData = {
      canonicalUrl: `${siteUrl}${ROUTE_STREAMTV}`,
      ogUrl: isPartner
        ? `${siteUrl}${getLink(ROUTE_PARTNER_STREAMTV, { partner: partner.slug })}`
        : `${siteUrl}${ROUTE_STREAMTV}`,
      title: partner?.meta?.title || 'Find Your Streaming Service - MyBundle',
      ogTitle: partner?.meta?.title || 'MyBundle – Streaming TV advice for YOUR needs',
      description: partner?.meta?.description || "Stop wasting money on the TV you don't watch.",
      image: partner?.meta?.image || defaultOGImg,
    };

    return (
      <Layout>
        {!partnerLoading && (
          <Helmet onChangeClientState={this.handleChangeHelmetData}>
            <title>{seoData.title}</title>
            <meta property="og:title" content={seoData.ogTitle} />
            <meta property="og:site_name" content="MyBundle" />
            <link rel="canonical" href={seoData.canonicalUrl} />
            <meta property="og:url" content={seoData.ogUrl} />
            <meta property="og:description" content={seoData.description} />
            <meta name="description" content={seoData.description} />
            <meta property="og:type" content="website" />
            <meta property="fb:app_id" content="608514030003288" />
            <meta property="og:image" content={seoData.image} />
            <meta name="robots" content="index, follow, max-image-preview:large" />
            <meta name="twitter:title" content={seoData.ogTitle} />
            <meta name="twitter:description" content={seoData.description} />
            <meta name="twitter:image" content={seoData.image} />
            <meta name="twitter:site" content="@mybundletv" />
            <meta name="twitter:creator" content="@mybundletv" />
            <meta name="twitter:card" content="summary" />
          </Helmet>
        )}
        <Container className="partner-home-page">
          <Welcome partner={partner} isIframe={isIframe} />
          {!partnerLoading && (
            <Guard partner={{ search_movies_shows: true, use_authorizing: true }}>
              <ShowsAndMovies partner={partner} />
            </Guard>
          )}
          <FreeTVSection />
          <Questions />
          <SellInternet />
        </Container>
      </Layout>
    );
  }
}

PartnerStreamTV.propTypes = {
  router: PropTypes.object.isRequired,
  isIframe: PropTypes.bool.isRequired,
  partner: PropTypes.object.isRequired,
  partnerLoading: PropTypes.bool.isRequired,
};

const mapStateToProps = ({ app, partner }) => ({
  isIframe: app.isIframe,
  partner: partner.data,
  partnerLoading: partner.loadings.partner,
});

export default connect(mapStateToProps, null)(withRouter(PartnerStreamTV));
